import React, { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Link from 'next/link';
import { useRouter } from 'next/compat/router';

import usePaths from 'src/hooks/use-paths';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import useMenuFilterOptions from 'src/hooks/use-menu-filter-options';
import useSpecialMenuCards from 'src/dispensary/hooks/use-special-menu-cards';

import ROUTES from 'src/routes';
import SHARED_ROUTES from 'shared/routes';
import { MenuType } from 'src/dispensary/core-menu/components/header/menu-type';

import useUI from 'src/hooks/use-ui';
import { ConciergeIcon } from 'src/components/kiosk-pro/kiosk-pro-shared/icons/concierge-icon';
import { useIsKioskConciergeEnabled } from 'src/concierge/use-is-kiosk-concierge-enabled';
import { useConciergeDisplayName } from 'src/concierge/use-concierge-display-name';
import { tracker } from 'src/utils/analytics';
import SvgChevronIcon from 'src/assets/chevron-icon';
import SvgExitIcon from 'src/assets/exit-icon';
import { HomeIcon } from 'src/components/kiosk-pro/kiosk-pro-shared/icons/home-icon';
import { CategoriesIcon } from 'src/components/kiosk-pro/kiosk-pro-shared/icons/categories-icon';
import { BrandsIcon } from 'src/components/kiosk-pro/kiosk-pro-shared/icons/brands-icon';
import { SpecialsIcon } from 'src/components/kiosk-pro/kiosk-pro-shared/icons/specials-icon';
import { CategoriesList } from 'src/components/kiosk-pro/kiosk-pro-layout/navigation/categories-list';
import { KioskProTutorialModal } from 'src/components/kiosk-pro/tutorial-modal';
import { useNavigation } from 'src/components/kiosk-pro/kiosk-pro-layout/navigation/use-navigation';
import { CategoriesMobileDisplayOption, CategoriesTypeOption, RadiusOption } from 'src/gql/graphql';
import {
  KioskProNavContainer,
  LogoSearchContainer,
  MenuTypeContainer,
  NavMenuContainer,
  TutorialNavLink,
  NavLink,
  HomeNavLink,
  CategoriesNavLink,
  LogoMenuRow,
  SearchContainer,
  CategoriesChevron,
  CategoriesAndCollectionsContainer,
  Divider,
  CategoriesOverlay,
  StyledKioskProLogo,
  StyledSearch,
  GradientBottom,
} from './navigation.styles';
import { CollectionsTextList } from './collections-text-list';

export const KioskProNavigation = (): JSX.Element => {
  const { href: baseHref } = usePaths();
  const { BRANDS, SPECIALS } = ROUTES;
  const { dispensary } = useDispensary();
  const { categories } = useDispensaryCategories();
  const router = useRouter();
  const flags = useFlags();
  const isTutorialEnabled = flags['growth.ecomm.kiosk-pro-tutorial.rollout'] ?? false;
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const { isConciergeActive } = useUI();
  const isConciergeEnabled = useIsKioskConciergeEnabled(dispensary);

  const isHomeActive = router?.asPath === baseHref;
  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- router can be null */
  const isCategoriesActive =
    (router?.asPath.includes(SHARED_ROUTES.PRODUCTS) || router?.asPath.includes(SHARED_ROUTES.COLLECTIONS)) &&
    !router?.asPath.includes(SHARED_ROUTES.BRANDS) &&
    !router?.asPath.includes(SHARED_ROUTES.SPECIALS);
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */
  const isBrandsActive = router?.asPath.includes(SHARED_ROUTES.BRANDS);
  const isSpecialsActive = router?.asPath.includes(SHARED_ROUTES.SPECIALS);

  const { brands } = useMenuFilterOptions({
    skipCurrentFilters: true,
  });
  // Kiosk sessions don't server-prefetch this query, so every cold reload starts with
  // hasSpecials=false while the request is in flight. Hiding the nav link during that
  // window makes Specials intermittently vanish on load even when specials exist
  // (ECOMM-11578) -- keep the link visible while the result is still unknown and only
  // hide it once the query has confirmed there are none.
  const { hasSpecials, loading: specialsLoading } = useSpecialMenuCards();
  const showSpecialsNav = hasSpecials || specialsLoading;

  const { webCustomizationSettingsV2 } = dispensary;
  const conciergeDisplayName = useConciergeDisplayName();
  const { style, type, mobileDisplay } = webCustomizationSettingsV2?.categories ?? {
    mobileDisplay: CategoriesMobileDisplayOption.grid,
    type: CategoriesTypeOption.large,
    style: RadiusOption.roundedRectangle,
  };

  const {
    isCategoriesOpen,
    handleCategoriesClick,
    handleCollectionClick,
    closeCategories,
    collections,
    trackEvent,
    hideDiscount,
    containerRef,
    bottomWatcherRef,
    hasOverflow,
    showGradient,
  } = useNavigation(dispensary);

  return (
    <>
      {isCategoriesOpen && <CategoriesOverlay onClick={closeCategories} />}
      <KioskProNavContainer $isCategoriesOpen={isCategoriesOpen} data-testid='kiosk-pro-navigation'>
        <LogoSearchContainer>
          <LogoMenuRow>
            <NavLink
              href={baseHref}
              onClick={() => {
                trackEvent('Home Logo');
                closeCategories();
              }}
              data-testid='kiosk-pro-nav-logo-link'
            >
              <StyledKioskProLogo />
            </NavLink>

            <MenuTypeContainer data-testid='kiosk-pro-menu-type-container'>
              <MenuType hideLabelOnSmallScreen={false} />
            </MenuTypeContainer>
          </LogoMenuRow>

          <SearchContainer data-testid='kiosk-pro-search-container'>
            <StyledSearch isKioskPro data-testid='kiosk-pro-search' />
          </SearchContainer>
        </LogoSearchContainer>

        <Divider />

        <NavMenuContainer
          $isConciergeEnabled={isConciergeEnabled}
          $hasSixItems={isConciergeEnabled && isTutorialEnabled}
          data-testid='kiosk-pro-nav-menu-container'
        >
          <HomeNavLink
            $isConciergeEnabled={isConciergeEnabled}
            as={Link}
            href={baseHref}
            onClick={() => {
              trackEvent('Home');
              closeCategories();
            }}
            data-testid='kiosk-pro-nav-home-link'
          >
            <HomeIcon isActive={isHomeActive} /> Home
          </HomeNavLink>

          <CategoriesNavLink
            $isConciergeEnabled={isConciergeEnabled}
            onClick={handleCategoriesClick}
            data-testid='kiosk-pro-nav-categories-link'
          >
            <CategoriesIcon isActive={isCategoriesActive} /> Categories{' '}
            <CategoriesChevron data-testid='kiosk-pro-categories-chevron'>
              {isCategoriesOpen ? <SvgExitIcon width='24px' /> : <SvgChevronIcon width='16px' />}
            </CategoriesChevron>
          </CategoriesNavLink>

          {isCategoriesOpen && (
            <CategoriesAndCollectionsContainer
              ref={containerRef}
              data-testid='kiosk-pro-categories-collections-container'
            >
              <CategoriesList categories={categories} style={style} type={type} mobileDisplay={mobileDisplay} />

              {collections.length > 0 && (
                <>
                  <Divider />

                  <CollectionsTextList
                    collections={collections}
                    handleCollectionClick={handleCollectionClick}
                    style={style}
                  />
                </>
              )}
              <div ref={bottomWatcherRef} style={{ height: '1px', marginTop: '20px' }} />

              {hasOverflow && <GradientBottom isVisible={showGradient} />}
            </CategoriesAndCollectionsContainer>
          )}

          {brands.length > 0 && (
            <NavLink
              $isConciergeEnabled={isConciergeEnabled}
              as={Link}
              href={`${baseHref}${BRANDS}`}
              onClick={() => {
                trackEvent('Brands');
                closeCategories();
              }}
              data-testid='kiosk-pro-nav-brands-link'
            >
              <BrandsIcon isActive={isBrandsActive} /> Brands
            </NavLink>
          )}

          {isTutorialEnabled && (
            <TutorialNavLink
              $isConciergeEnabled={isConciergeEnabled}
              onClick={() => setIsTutorialOpen(true)}
              data-testid='kiosk-pro-how-it-works-button'
            >
              How it works
            </TutorialNavLink>
          )}

          {showSpecialsNav && !hideDiscount && (
            <NavLink
              $isConciergeEnabled={isConciergeEnabled}
              as={Link}
              href={`${baseHref}${SPECIALS}`}
              onClick={() => {
                trackEvent('Specials');
                closeCategories();
              }}
              data-testid='kiosk-pro-nav-specials-link'
            >
              <SpecialsIcon isActive={isSpecialsActive} /> Specials
            </NavLink>
          )}

          {isConciergeEnabled && (
            <NavLink
              $isConciergeEnabled={isConciergeEnabled}
              as={Link}
              href={`${baseHref}/concierge`}
              onClick={() => {
                trackEvent('Concierge');
                tracker.conciergeEntryPointClicked({ location: 'kiosk_pro_nav' });
                closeCategories();
              }}
              data-testid='kiosk-pro-nav-concierge-link'
            >
              <ConciergeIcon isActive={isConciergeActive} aria-hidden />{' '}
              {conciergeDisplayName ? `Ask ${conciergeDisplayName}` : 'Virtual Budtender'}
            </NavLink>
          )}
        </NavMenuContainer>
      </KioskProNavContainer>
      {isTutorialEnabled && <KioskProTutorialModal isOpen={isTutorialOpen} onClose={() => setIsTutorialOpen(false)} />}
    </>
  );
};
